import React, { useState, useRef, useEffect } from "react";
import { useInput } from "react-admin";
import { useRecordContext } from "ra-core";
import { useForm } from "react-final-form";
import {
  Button,
  Typography,
  Box,
  LinearProgress,
  TextField,
} from "@material-ui/core";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { makeStyles } from "@material-ui/core/styles";
import { useVideoUpload } from "./VideoUploadContext";
import { PENDING_VIDEO_VALUE } from "./videoUpload";

const useStyles = makeStyles(() => ({
  wrapper: {
    width: "100%",
    margin: "8px 0 16px",
  },
  dropArea: {
    border: "1px dashed #b89c3a",
    borderRadius: 8,
    padding: 20,
    textAlign: "center",
    background: "#fbf9f2",
  },
  preview: {
    width: "100%",
    height: 340,
    marginTop: 12,
    borderRadius: 8,
    background: "#000",
    objectFit: "contain",
    display: "block",
  },
  pathBox: {
    marginTop: 8,
    minHeight: 32,
    padding: "6px 10px",
    background: "#f1efe9",
    borderRadius: 4,
    fontFamily: "monospace",
    fontSize: 12,
    wordBreak: "break-all",
    display: "flex",
    alignItems: "center",
  },
  statusRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginTop: 8,
    minHeight: 24,
  },
  statusSuccess: {
    color: "#2e7d32",
  },
  statusPending: {
    color: "#b89c3a",
  },
  statusError: {
    color: "#d32f2f",
  },
  actions: {
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: 8,
  },
  introTimeWrapper: {
    display: "flex",
    justifyContent: "center",
    marginTop: 16,
  },
  introTimeField: {
    minWidth: 400,
  },
}));

const IntroTimeField = ({ source, label, validate, required: isRequired }) => {
  const classes = useStyles();
  const {
    input: { value, onChange, onBlur, name },
    meta: { touched, error },
  } = useInput({ source, validate });

  return (
    <div className={classes.introTimeWrapper}>
      <TextField
        className={classes.introTimeField}
        name={name}
        label={label}
        type="number"
        value={value ?? ""}
        onChange={event => {
          const nextValue = event.target.value;
          onChange(nextValue === "" ? null : Number(nextValue));
        }}
        onBlur={onBlur}
        inputProps={{ min: 0, step: 1 }}
        required={isRequired}
        size="small"
        variant="outlined"
        error={Boolean(touched && error)}
        helperText={touched && error}
      />
    </div>
  );
};

const validateVideoField = (source, value, pendingFile, required = false) => {
  if (value === PENDING_VIDEO_VALUE || pendingFile) {
    return undefined;
  }

  if (required && !value) {
    return "Please select a video";
  }

  if (value && !/^videos\//.test(value)) {
    return "Video path must start with videos/";
  }

  return undefined;
};

const VideoUploadInput = ({
  source = "video_file_path",
  label = "Video",
  previewUrl,
  previewUrlSource = "preview_url",
  required = false,
  clearable = false,
  introTimeSource,
  introTimeLabel = "Intro Time (seconds)",
  introTimeValidate,
  introTimeRequired = false,
  ...props
}) => {
  const classes = useStyles();
  const fileInputRef = useRef(null);
  const form = useForm();
  const record = useRecordContext(props);
  const {
    setPendingFile,
    getPendingFile,
    clearPendingFile,
    clearUploadError,
    getUploadError,
    getUploadProgress,
    hasPendingFile,
  } = useVideoUpload();
  const pendingFile = getPendingFile(source);
  const uploadError = getUploadError(source);
  const uploadProgress = getUploadProgress(source);
  const isUploading = uploadProgress != null;

  const {
    input: { value, onChange },
    meta: { touched, error },
  } = useInput({
    source,
    validate: fieldValue =>
      validateVideoField(source, fieldValue, getPendingFile(source), required),
    ...props,
  });

  const [localPreview, setLocalPreview] = useState(null);
  const [previewCleared, setPreviewCleared] = useState(false);
  const savedPreviewUrl =
    previewUrl || (record && record[previewUrlSource]);
  const videoSrc =
    !previewCleared &&
    (localPreview || (!hasPendingFile(source) && savedPreviewUrl));

  useEffect(() => {
    return () => {
      if (localPreview) URL.revokeObjectURL(localPreview);
    };
  }, [localPreview]);

  const handleSelectClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileChange = event => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file) return;

    const objectUrl = URL.createObjectURL(file);
    setLocalPreview(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return objectUrl;
    });

    setPendingFile(source, file);
    setPreviewCleared(false);
    onChange(PENDING_VIDEO_VALUE);
  };

  const handleClear = () => {
    if (isUploading) return;

    clearPendingFile(source);
    clearUploadError(source);
    setLocalPreview(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setPreviewCleared(true);
    onChange(null);
    if (introTimeSource) {
      form.change(introTimeSource, null);
    }
  };

  const hasSavedPath =
    Boolean(value) &&
    value !== PENDING_VIDEO_VALUE &&
    /^videos\//.test(value);

  const isAwaitingUpload =
    Boolean(pendingFile) ||
    value === PENDING_VIDEO_VALUE ||
    Boolean(uploadError);

  const pendingLabel = pendingFile?.name || "Selected video";
  const canRemove =
    clearable &&
    !isUploading &&
    (hasSavedPath || isAwaitingUpload || Boolean(savedPreviewUrl && !previewCleared));

  const hasVideo =
    hasSavedPath ||
    isAwaitingUpload ||
    isUploading ||
    Boolean(savedPreviewUrl && !previewCleared);

  const showDetails = hasVideo;

  const statusConfig = (() => {
    if (uploadError && isAwaitingUpload) {
      return {
        icon: <ErrorIcon fontSize="small" />,
        message: uploadError,
        className: classes.statusError,
      };
    }
    if (isUploading) {
      return {
        icon: <CheckCircleIcon fontSize="small" />,
        message: "Uploading video...",
        className: classes.statusPending,
      };
    }
    if (isAwaitingUpload) {
      return {
        icon: <CheckCircleIcon fontSize="small" />,
        message: "Ready to upload on Save",
        className: classes.statusPending,
      };
    }
    if (hasSavedPath) {
      return {
        icon: <CheckCircleIcon fontSize="small" />,
        message: "Video saved",
        className: classes.statusSuccess,
      };
    }
    return null;
  })();

  const pathDisplay = (() => {
    if (isUploading || isAwaitingUpload) {
      return pendingLabel;
    }
    if (hasSavedPath) {
      return value;
    }
    return null;
  })();

  return (
    <div className={classes.wrapper}>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>

      <div className={classes.dropArea}>
        <input
          ref={fileInputRef}
          type="file"
          accept="video/*"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        <div className={classes.actions}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<CloudUploadIcon />}
            onClick={handleSelectClick}
            disabled={isUploading}
          >
            {isAwaitingUpload || hasSavedPath ? "Replace Video" : "Choose Video"}
          </Button>
          {canRemove && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<DeleteOutlineIcon />}
              onClick={handleClear}
            >
              Remove Video
            </Button>
          )}
        </div>
        {introTimeSource && hasVideo && (
          <IntroTimeField
            source={introTimeSource}
            label={introTimeLabel}
            validate={introTimeValidate}
            required={introTimeRequired}
          />
        )}
        <Typography variant="caption" display="block" style={{ marginTop: 8 }}>
          Video uploads to S3 when you click Save.
        </Typography>
      </div>

      {showDetails && statusConfig && (
        <div className={`${classes.statusRow} ${statusConfig.className}`}>
          {statusConfig.icon}
          <Typography variant="body2">{statusConfig.message}</Typography>
        </div>
      )}

      {showDetails && pathDisplay && (
        <div className={classes.pathBox}>{pathDisplay}</div>
      )}

      {isUploading && (
        <Box mt={1}>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <Typography variant="caption">{uploadProgress}% uploaded</Typography>
        </Box>
      )}

      {videoSrc && (
        <video className={classes.preview} src={videoSrc} controls />
      )}

      {touched && error && (
        <Typography variant="body2" className={classes.statusError} style={{ marginTop: 8 }}>
          {error}
        </Typography>
      )}
    </div>
  );
};

export default VideoUploadInput;
